/**
 * Event List utility functions
 * Centralized event formatting and calculation functions for list views
 */

import moment from 'moment';
import { EVENT_TYPES, DATE_TYPES } from '../constants/events';
import { toJsDate } from './dateUtils';
import { computeEventStatus } from './eventStatus';
import { getEventRouteKey } from './eventIdentity';

export { getEventRouteKey, getEventDbId, withEventSlugAsRaId } from './eventIdentity';

/**
 * Format event name with truncation
 * @param {string} name - Event name
 * @param {number} maxLength - Maximum length (default: 50)
 * @returns {string} Formatted event name
 */
export const formatEventName = (name, maxLength = 50) => {
  if (!name) return 'N/A';
  return name.length > maxLength ? `${name.substring(0, maxLength)}...` : name;
};

/**
 * Format event type and date type display
 * @param {number} type - Event type ID
 * @param {string} dateType - Date type ID
 * @returns {string} Formatted type display
 */
export const formatEventType = (type, dateType) => {
  const eventType = EVENT_TYPES.find((t) => t.id === type);
  const dateTypeObj = DATE_TYPES.find((t) => t.id === dateType);
  
  if (!eventType || !dateTypeObj) return 'N/A';
  
  return `${eventType.name} / ${dateTypeObj.name}`;
};

/**
 * Format event date for display
 * @param {Object} date - Firebase timestamp or Date object
 * @param {string} format - Moment format string
 * @returns {string} Formatted date string
 */
export const formatEventDate = (date, format = 'DD/MM/YY / HH:mm') => {
  if (!date) return 'N/A';

  try {
    const d = toJsDate(date);
    return d ? moment(d).format(format) : 'N/A';
  } catch (error) {
    console.error('Error formatting event date:', error);
    return 'N/A';
  }
};

/**
 * Get event verification and visibility status
 * @param {Object} event - Event object
 * @returns {Object} Status information
 */
export const getEventVerificationStatus = (event) => {
  if (!event) return { verified: 'N/A', active: 'N/A', verifiedColor: 'grey', activeColor: 'grey' };
  
  const verified = event.isVerified ? 'Yes' : 'No';
  const active = event.isActive ? 'Yes' : 'No';
  
  return {
    verified,
    active,
    verifiedColor: event.isVerified ? 'green' : 'red',
    activeColor: event.isActive ? 'green' : 'red',
  };
};

/** @deprecated computeEventStatus kullan — geri uyumluluk icin alias */
export const getEventStatus = (event, options) => {
  const s = computeEventStatus(event, options);
  return {
    status: s.label,
    bgColor: s.bgColor,
    color: s.color,
    key: s.key,
  };
};

/**
 * Format currency for display
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: 'TRY')
 * @param {string} locale - Locale (default: 'tr-TR')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'TRY', locale = 'tr-TR') => {
  if (amount === null || amount === undefined || isNaN(amount)) return 'N/A';
  
  try {
    return amount.toLocaleString(locale, { 
      style: 'currency', 
      currency: currency 
    });
  } catch (error) {
    console.error('Error formatting currency:', error);
    return 'N/A';
  }
};

/**
 * Get event action URL (panel). Prefers slug as RA id.
 * @param {string|object} eventOrId - Event object or id/slug
 * @param {string} action - Action type ('show', 'edit', 'create')
 * @returns {string} Action URL
 */
export const getEventActionUrl = (eventOrId, action = 'show') => {
  const baseUrl = '/#/events-v2';
  if (action === 'create') return `${baseUrl}/create`;
  const key = getEventRouteKey(eventOrId);
  if (!key) return baseUrl;
  const actionUrls = {
    show: `${baseUrl}/${encodeURIComponent(key)}/show`,
    edit: `${baseUrl}/${encodeURIComponent(key)}`,
  };
  return actionUrls[action] || baseUrl;
};

/**
 * Check if user has permission to view events
 * @param {Object} permissions - User permissions object
 * @returns {Object} Permission status
 */
export const checkEventPermissions = (permissions) => {
  if (!permissions) {
    return { canView: false, isManager: false, reason: 'No permissions loaded' };
  }

  const isAdmin = permissions.crole === 'admin';
  const isOperator = permissions.crole === 'operator';
  const isManager = (permissions.crole === 'organisationCompanyManager' || 
                    permissions.crole === 'venueManager') && permissions.rid;

  const canView = isAdmin || isOperator || isManager;

  return {
    canView,
    isManager,
    isAdmin,
    isOperator,
    role: permissions.crole,
    rid: permissions.rid,
  };
};

/**
 * Build Firestore query based on user permissions
 * @param {Object} permissions - User permissions
 * @param {Object} options - Query options
 * @returns {Object} Firestore query configuration
 */
/** API modunda organisation/venue manager filtreleri (Firebase array-contains paritesi). */
export const getManagerApiFilters = (permissions) => {
  const apiFilters = {};
  if (permissions?.crole === 'organisationCompanyManager' && permissions?.rid) {
    apiFilters.organisationCompanies = permissions.rid;
  }
  if (permissions?.crole === 'venueManager' && permissions?.rid) {
    apiFilters.venue = permissions.rid;
  }
  return apiFilters;
};

export const buildEventQuery = (permissions, options = {}) => {
  const {
    orderBy = 'startdate',
    orderDirection = 'asc',
    limitCount = 100,
    startDate = moment().subtract(7, 'hours').toDate(),
  } = options;

  const baseQuery = {
    orderBy,
    orderDirection,
    limit: limitCount,
    startDate,
  };

  if (permissions.crole === 'organisationCompanyManager' && permissions.rid) {
    return {
      ...baseQuery,
      where: [
        ['startdate', '>=', startDate],
        ['organisationCompanies', 'array-contains', permissions.rid],
      ],
    };
  }

  if (permissions.crole === 'venueManager' && permissions.rid) {
    return {
      ...baseQuery,
      where: [
        ['startdate', '>=', startDate],
        ['venue', '==', permissions.rid],
      ],
    };
  }

  // Admin or operator - no additional filters
  return {
    ...baseQuery,
    where: [['startdate', '>=', startDate]],
  };
};

const eventListUtils = {
  formatEventName,
  formatEventType,
  formatEventDate,
  getEventVerificationStatus,
  getEventStatus,
  formatCurrency,
  getEventActionUrl,
  checkEventPermissions,
  buildEventQuery,
};

export default eventListUtils;
